import { useContext } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import * as Scrivito from "scrivito"
import { ScrivitoLink } from "scrivito/scrivito-link/ScrivitoLink"
import {
  NotificationContext,
  isNotificationType
} from "shared/context/NotificationContext"
import { useAuth } from "shared/lib/useAuth"
import { IconSize } from "shared/types/Icon"
import { Chip, ChipVariant } from "shared/ui/chip/Chip"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { LinkList } from "shared/ui/link-list/LinkList"
import { Tooltip } from "shared/ui/tooltip/Tooltip"

import { LinkListIconLinkWidget } from "./LinkListIconLinkWidgetClass"

Scrivito.provideComponent(LinkListIconLinkWidget, ({ widget }) => {
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()

  const link = widget.get("link") as Scrivito.Link
  const linkTitle = link?.title()
  const icon = widget.get("icon") as IconName
  const hideTooltip = widget.get("hideTooltip")

  const notificationContext = useContext(NotificationContext)
  const notificationType = widget.get("notificationType") || ""
  const notificationCount = isNotificationType(notificationType)
    ? notificationContext?.[notificationType] ?? 0
    : 0

  const tooltipText =
    notificationCount > 0 ? `${linkTitle} (${notificationCount})` : linkTitle

  const currentPage = Scrivito.currentPage()
  const target = link?.obj()
  const isCurrentPage =
    target?.id() && currentPage?.id() && target?.id() === currentPage?.id()
  const isAncestorActive = currentPage
    ?.ancestors()
    .some(page => page?.id() && page?.id() === target?.id())

  const isLoginRequired = target?.get("isLoginRequired")
  const isLocked = isLoginRequired && !isAuthenticated

  const tooltipContent = isLocked
    ? `${linkTitle} (${t("Authentication.SignInAndUnlock")})`
    : linkTitle

  return (
    <Tooltip content={tooltipContent} placement="right" disabled={hideTooltip}>
      <LinkList.GenericWrapper>
        <ScrivitoLink
          className={classNames("link", { "link--locked": isLocked })}
          to={link}
          label={tooltipText}
          aria-label={tooltipText}
          data-current-page={isCurrentPage ? "" : undefined}
          data-ancestor-page={isAncestorActive ? "" : undefined}
        >
          <div className="link__icon">
            {icon ? (
              <Icon
                className={classNames({
                  "link__icon--subtle": isLocked,
                  "icon--notification": notificationCount > 0
                })}
                name={icon}
              />
            ) : null}
            {isLocked ? (
              <Icon
                className="link__icon--locked"
                name={IconName.LOCK_SOLID}
                size={IconSize.TEXT}
              />
            ) : null}
          </div>
          <div className="link__content">
            <span className="link__label">{linkTitle}</span>
            {isLocked ? (
              <span className="link__subtitle">
                {t("Authentication.SignInAndUnlock")}
              </span>
            ) : null}
          </div>
          {notificationCount > 0 ? (
            <Chip variant={ChipVariant.NOTIFICATION} aria-hidden>
              {notificationCount}
            </Chip>
          ) : null}
        </ScrivitoLink>
      </LinkList.GenericWrapper>
    </Tooltip>
  )
})
